import { Box, Container, Typography, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useLanguage } from '../contexts/LanguageContext';

const SectionContainer = styled(Box)(({ theme }) => ({
  padding: '120px 0',
  background: theme.palette.background.default,
  position: 'relative',
  overflow: 'hidden',
}));

const StatCard = styled(Box)(({ theme }) => ({
  padding: '48px 32px',
  borderRadius: '24px',
  background: theme.palette.mode === 'dark'
    ? 'rgba(30, 41, 59, 0.6)'
    : 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(20px)',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
  textAlign: 'center',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
    transform: 'scaleX(0)',
    transition: 'transform 0.4s ease',
  },
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 20px 60px rgba(37, 99, 235, 0.3)'
      : '0 20px 60px rgba(37, 99, 235, 0.2)',
    '&::before': {
      transform: 'scaleX(1)',
    },
  },
}));

const AnimatedNumber = styled(Typography)(({ theme }) => ({
  fontSize: '4rem',
  fontWeight: 900,
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  lineHeight: 1,
  marginBottom: '16px',
}));

const FloatingCircle = styled(Box)(({ theme }) => ({
  position: 'absolute',
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${theme.palette.primary.main}20 0%, ${theme.palette.secondary.main}20 100%)`,
  filter: 'blur(60px)',
  animation: 'float 15s ease-in-out infinite',
}));

export default function WhySection() {
  const { t } = useLanguage();

  const stats = [
    {
      value: t('why.experience.value'),
      label: t('why.experience.title'),
    },
    {
      value: t('why.projects.value'),
      label: t('why.projects.title'),
    },
    {
      value: t('why.clients.value'),
      label: t('why.clients.title'),
    },
    {
      value: t('why.team.value'),
      label: t('why.team.title'),
    },
  ];

  return (
    <SectionContainer component="section" id="about" aria-labelledby="why-heading">
      <FloatingCircle sx={{ width: 300, height: 300, top: '10%', left: '-5%' }} />
      <FloatingCircle sx={{ width: 400, height: 400, bottom: '10%', right: '-10%', animationDelay: '-7s' }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Stack spacing={2} alignItems="center" textAlign="center" sx={{ mb: 8 }}>
          <Typography
            id="why-heading"
            variant="h2"
            component="h2"
            sx={{
              fontWeight: 800,
              background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {t('why.title')}
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: 600 }}
          >
            {t('why.subtitle')}
          </Typography>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)',
            },
            gap: 3,
          }}
        >
          {stats.map((stat, index) => (
            <StatCard key={index}>
              <AnimatedNumber>{stat.value}</AnimatedNumber>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ fontWeight: 600 }}
              >
                {stat.label}
              </Typography>
            </StatCard>
          ))}
        </Box>

        {/* Team Image */}
        <Box
          sx={{
            mt: 8,
            borderRadius: '32px',
            overflow: 'hidden',
            position: 'relative',
            boxShadow: (theme) => theme.palette.mode === 'dark'
              ? '0 30px 80px rgba(0, 0, 0, 0.5)'
              : '0 30px 80px rgba(0, 0, 0, 0.15)',
          }}
        >
          <img
            src="https://images.unsplash.com/photo-1690378820474-b468b8ee64d3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTAwNDR8MHwxfHNlYXJjaHwxfHx0ZWFtJTIwb2ZmaWNlJTIwY29sbGFib3JhdGlvbiUyMGJ1c2luZXNzfGVufDB8MHx8fDE3NTkyNzQ4NTF8MA&ixlib=rb-4.1.0&q=85"
            alt="Professional team working together - Lyubomyr (Lou) Reverchuk on Unsplash"
            style={{
              width: '100%',
              height: '500px',
              objectFit: 'cover',
            }}
          />
        </Box>
      </Container>
    </SectionContainer>
  );
}